import React, { useEffect, useState } from 'react';
import { Table, Avatar, Tag, Spin, Empty, Tooltip } from 'antd';
import { UserOutlined, ClockCircleOutlined, CalendarOutlined } from '@ant-design/icons';
import GetLoginAuditApi from '../../api/login-audit/GetLoginAuditApi';
import moment from 'moment';

function LoginAudit() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    const res = await GetLoginAuditApi();
    if (res?.success) {
      setData(res.data);
    }
    setLoading(false);
  }

  const expandedRowRender = (record) => {
    const historyColumns = [
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        render: (date) => (
          <span className="flex items-center gap-1 text-gray-600">
            <CalendarOutlined className="mr-1" />
            {moment(date).format('ddd, MMM D YYYY')}
          </span>
        ),
      },
      {
        title: 'Login Time',
        dataIndex: 'loginTime',
        key: 'loginTime',
        render: (time) =>
          time ? (
            <Tag color="blue" icon={<ClockCircleOutlined />}>
              {moment(time).format('hh:mm A')}
            </Tag>
          ) : (
            <Tag color="default">No login</Tag>
          ),
      },
    ];

    return (
      <Table
        columns={historyColumns}
        dataSource={record.history.map((h, i) => ({ ...h, key: i }))}
        pagination={false}
        size="small"
        className="ml-12"
      />
    );
  };

  const columns = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <Avatar
            src={record.imageUrl}
            icon={!record.imageUrl && <UserOutlined />}
            size={36}
          />
          <div>
            <div className="font-medium">
              {record.firstName} {record.lastName}
            </div>
            {record.employeeId && (
              <div className="text-xs text-gray-400">{record.employeeId}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Designation',
      dataIndex: 'designationName',
      key: 'designationName',
      render: (val) => val || <span className="text-gray-400">—</span>,
    },
    {
      title: 'Unit',
      dataIndex: 'unitName',
      key: 'unitName',
      render: (val) => val || <span className="text-gray-400">—</span>,
    },
    {
      title: "Today's Login",
      dataIndex: 'todayLoginTime',
      key: 'todayLoginTime',
      render: (time) =>
        time ? (
          <Tooltip title={moment(time).format('YYYY-MM-DD HH:mm:ss')}>
            <Tag color="green" icon={<ClockCircleOutlined />}>
              {moment(time).format('hh:mm A')}
            </Tag>
          </Tooltip>
        ) : (
          <Tag color="default">Not logged in</Tag>
        ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-4">
        <div className="text-xl font-semibold">Login Audit</div>
        <div className="text-gray-500 text-sm mt-1">
          View today's login times and expand each row to see the past 5 working days.
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spin size="large" />
        </div>
      ) : data.length === 0 ? (
        <Empty description="No login data available" />
      ) : (
        <Table
          columns={columns}
          dataSource={data.map((d) => ({ ...d, key: d.userId }))}
          expandable={{
            expandedRowRender,
            expandRowByClick: false,
            rowExpandable: () => true,
          }}
          pagination={{ pageSize: 20 }}
          size="middle"
          bordered
        />
      )}
    </div>
  );
}

export default LoginAudit;
